import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';

import { createLogger } from '../../utils/logger';
import { analytics, AnalyticsEvent } from '../../services/analytics';
import { setAuthToken } from '../../services/auth/tokenStorage';
import { onboardingService } from '../../services/onboarding';
const logger = createLogger('EmailVerificationResult');

interface EmailVerificationResultScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<{ params: { token: string } }, 'params'>;
}

const EmailVerificationResultScreen: React.FC<EmailVerificationResultScreenProps> = ({
  navigation,
  route,
}) => {
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const token = route.params?.token;

    // Add a small delay before showing any error to prevent brief flash
    // This helps avoid showing errors while navigation is settling
    const showErrorWithDelay = (errorMessage: string) => {
      setTimeout(() => {
        setStatus('error');
        setMessage(errorMessage);
      }, 300);
    };

    if (!token) {
      showErrorWithDelay('No verification token found.');
      return;
    }

    const verify = async () => {
      try {
        // Check if token looks like a JWT (has 3 parts separated by dots)
        const isJWT = token.split('.').length === 3;

        if (isJWT) {
          // Token is already a JWT from GET endpoint redirect
          // Just store it and mark email as verified
          logger.debug('📧 JWT token received, storing and marking verified');

          await AsyncStorage.setItem('@email_verified', 'true');
          await setAuthToken(token);

          // Set success after storage is complete
          setStatus('success');
          setMessage('Your email has been verified!');
          analytics.track(AnalyticsEvent.EmailVerified, { path: 'direct_token' });
          return;
        }

        // Token is a verification token, call POST endpoint
        logger.debug('📧 Verification token received, calling API');
        const res = await fetch(`${API_URL}/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (res.ok) {
          // Store email verification status first
          await AsyncStorage.setItem('@email_verified', 'true');

          // Store token in the keychain
          if (data.token || data.access_token) {
            const authToken = data.token || data.access_token;
            await setAuthToken(authToken);
          }

          // Set success after storage is complete
          setStatus('success');
          setMessage(data.message || 'Your email has been verified!');
          analytics.track(AnalyticsEvent.EmailVerified, { path: 'api' });
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed. The link may have expired.');
        }
      } catch (err) {
        logger.error('Email verification error:', err);
        setStatus('error');
        setMessage('Network error. Please check your connection and try again.');
      }
    };

    verify();
  }, [route.params]);

  const handleContinue = async () => {
    // Letter-first signups (onboardingVersion 2) skip security setup
    // entirely — verification leads straight to the letter. TOTP remains
    // available in Settings, and is offered after upgrade (plan Phase 8).
    try {
      const { onboardingVersion } = await onboardingService.getProgressAndWizardState();
      if (onboardingVersion >= 2) {
        logger.debug('Letter-first user verified — routing to the letter hook');
        navigation.reset({
          index: 0,
          routes: [
            {
              name: 'LetterFirst',
              state: { routes: [{ name: 'LetterHook' }] },
            },
          ],
        });
        return;
      }
    } catch (err) {
      logger.error('Onboarding state check failed, falling back to Dashboard', err);
    }

    // Invited accounts (successors, keyholders) come here with the
    // invitation waiting; verification leads straight back to it. Two-
    // factor is offered after an upgrade and stays available in Settings,
    // so nobody is sent to the Security Chooser from here.
    try {
      const invitationToken = await AsyncStorage.getItem('@invitation_token');
      if (invitationToken) {
        logger.debug('Invited user verified - returning to the invitation');
        navigation.reset({
          index: 0,
          routes: [{ name: 'AcceptInvitation', params: { token: invitationToken } }],
        });
        return;
      }
    } catch (err) {
      logger.error('Invitation token check failed', err);
    }

    logger.debug('Verified - navigating to Dashboard');
    navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
  };

  const handleBackToLogin = () => {
    logger.debug('Navigating back to Login screen');
    // Navigate to Auth stack, which contains the Login screen
    navigation.reset({
      index: 0,
      routes: [
        {
          name: 'Auth',
          state: {
            routes: [{ name: 'Login' }],
          },
        },
      ],
    });
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const email = await AsyncStorage.getItem('@signup_email');

      if (!email) {
        setMessage('Unable to resend email. Please sign up again.');
        setIsResending(false);
        return;
      }

      logger.debug('Resending verification email to:', email);

      const response = await fetch(`${API_URL}/auth/send-email-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setMessage('A new verification email has been sent. Please check your inbox.');
      } else {
        const data = await response.json();
        setMessage(data.message || 'Failed to resend verification email. Please try again.');
      }
    } catch (error) {
      logger.error('Failed to resend email:', error);
      setMessage('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.content}>
        <View style={styles.iconContainer}>
          {status === 'verifying' && (
            <ActivityIndicator size="large" color="#29BFC0" />
          )}
          {status === 'success' && (
            <Text style={styles.iconText}>✅</Text>
          )}
          {status === 'error' && (
            <Text style={styles.iconText}>❌</Text>
          )}
        </View>

        <View style={styles.header}>
          <Text style={styles.title}>
            {status === 'verifying' && 'Verifying Email'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </Text>
          <Text style={styles.subtitle}>{message}</Text>
        </View>

        {status === 'success' && (
          <>
            <View style={styles.successBox}>
              <Text style={styles.successText}>
                Your email has been successfully verified. Continue to set up two-factor authentication to keep your account secure.
              </Text>
            </View>

            <TouchableOpacity
              style={styles.continueButton}
              onPress={handleContinue}
            >
              <Text style={styles.continueButtonText}>Continue</Text>
            </TouchableOpacity>
          </>
        )}

        {status === 'error' && (
          <>
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>
                The verification link may have expired or is invalid. You can request a new verification email or try logging in.
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.resendButton, isResending && styles.buttonDisabled]}
              onPress={handleResendEmail}
              disabled={isResending}
            >
              {isResending ? (
                <ActivityIndicator color="#29BFC0" />
              ) : (
                <Text style={styles.resendButtonText}>Resend Verification Email</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.backButton, isResending && styles.buttonDisabled]}
              onPress={handleBackToLogin}
              disabled={isResending}
            >
              <Text style={styles.backButtonText}>Back to Login</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  content: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    marginTop: ms(60),
    paddingHorizontal: ms(20),
    paddingTop: ms(40),
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: ms(20),
    minHeight: s(80),
    justifyContent: 'center',
  },
  iconText: {
    fontSize: ms(64, 0.3),
  },
  header: {
    alignItems: 'center',
    marginBottom: ms(30),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
    paddingHorizontal: ms(10),
  },
  successBox: {
    backgroundColor: '#D4EDDA',
    borderRadius: ms(12),
    padding: ms(20),
    marginBottom: ms(30),
    borderWidth: ms(1),
    borderColor: '#C3E6CB',
  },
  successText: {
    fontSize: ms(15, 0.3),
    color: '#155724',
    textAlign: 'center',
    lineHeight: ms(22, 0.3),
  },
  errorBox: {
    backgroundColor: '#F8D7DA',
    borderRadius: ms(12),
    padding: ms(20),
    marginBottom: ms(25),
    borderWidth: ms(1),
    borderColor: '#F5C6CB',
  },
  errorText: {
    fontSize: ms(15, 0.3),
    color: '#721C24',
    textAlign: 'center',
    lineHeight: ms(22, 0.3),
  },
  continueButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(20),
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  resendButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(15),
  },
  resendButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  backButton: {
    backgroundColor: '#d8dbdd',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backButtonText: {
    color: '#333',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

export default EmailVerificationResultScreen; 